import { useState } from 'react';
import { useHayCambios } from '../../hooks/useHayCambios';
import { useCierreConfirmado } from '../../hooks/useCierreConfirmado';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { api, extractErrorMessage } from '../../api/client';
import { useToast } from '../../components/Toast';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { Confirm } from '../../components/Confirm';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import type { Cuenta, TipoCuenta } from './types';
import { TIPOS_CUENTA, etiquetaTipo, fechaCorta, hoyAR, inputCls, pesos } from './formato';
import { CobrosMercadoPagoPanel } from './CobrosMercadoPagoPanel';

// ============================================================
// Cuentas y saldos
// ============================================================

/** Saldo en línea de una cuenta con integración: importe y hora de la lectura, o el error (se usa el último manual). */
function CeldaSaldoEnLinea({ c }: { c: Cuenta }) {
  const s = c.saldoEnLinea;
  if (!s) return <span className="text-slate-400">—</span>;
  if (s.error || s.saldo == null) {
    return <span className="text-xs text-amber-700" title={s.error ?? undefined}>No se pudo leer: se usa el último saldo manual</span>;
  }
  return (
    <span className="whitespace-nowrap">
      <span className={s.saldo < 0 ? 'text-red-600' : ''}>{pesos(s.saldo)}</span>
      {s.actualizadoEn && <span className="block text-xs text-slate-500">automático · {new Date(s.actualizadoEn).toLocaleString('es-AR')}</span>}
    </span>
  );
}

/** diasSaldoViejo viene del flujo de caja (umbral del backend); mientras carga no se muestra. */
export function CuentasTab({ onCambio, diasSaldoViejo }: { onCambio: () => void; diasSaldoViejo?: number }) {
  const { can } = useAuth();
  const toast = useToast();
  const { data, loading, refetch } = useApi<Cuenta[]>('/api/v3/finanzas/cuentas');
  const [editando, setEditando] = useState<{ id?: number; nombre: string; tipo: TipoCuenta; activa: boolean } | null>(null);
  const [saldoDe, setSaldoDe] = useState<{ cuenta: Cuenta; fecha: string; saldo: string } | null>(null);
  const [borrar, setBorrar] = useState<Cuenta | null>(null);
  const [guardando, setGuardando] = useState(false);
  const cierreCuenta = useCierreConfirmado(useHayCambios(editando, !!editando), () => setEditando(null));
  const cierreSaldo = useCierreConfirmado(useHayCambios(saldoDe, !!saldoDe), () => setSaldoDe(null));
  const puedeEditar = can('pagos', 'editar');
  const puedeEliminar = can('pagos', 'eliminar');

  const recargar = () => { refetch(); onCambio(); };

  const guardarCuenta = async () => {
    if (!editando || !editando.nombre.trim()) { toast.addToast('El nombre es obligatorio', 'error'); return; }
    setGuardando(true);
    try {
      const body = { nombre: editando.nombre.trim(), tipo: editando.tipo, activa: editando.activa };
      if (editando.id) await api.patch(`/api/v3/finanzas/cuentas/${editando.id}`, body);
      else await api.post('/api/v3/finanzas/cuentas', body);
      toast.addToast(editando.id ? 'Cuenta actualizada' : 'Cuenta creada', 'success');
      setEditando(null);
      recargar();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); } finally { setGuardando(false); }
  };

  const guardarSaldo = async () => {
    if (!saldoDe) return;
    const saldo = Number(saldoDe.saldo.replace(',', '.'));
    if (!saldoDe.fecha || saldoDe.saldo.trim() === '' || !Number.isFinite(saldo)) { toast.addToast('Completá fecha y saldo', 'error'); return; }
    setGuardando(true);
    try {
      await api.post(`/api/v3/finanzas/cuentas/${saldoDe.cuenta.id}/saldos`, { fecha: saldoDe.fecha, saldo });
      toast.addToast('Saldo cargado', 'success');
      setSaldoDe(null);
      recargar();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); } finally { setGuardando(false); }
  };

  const eliminar = async () => {
    if (!borrar) return;
    try {
      await api.delete(`/api/v3/finanzas/cuentas/${borrar.id}`);
      toast.addToast('Cuenta eliminada', 'success');
      recargar();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); } finally { setBorrar(null); }
  };

  const columns = [
    { key: 'nombre', header: 'Cuenta', render: (c: Cuenta) => <span className="font-medium">{c.nombre}</span> },
    { key: 'tipo', header: 'Tipo', render: (c: Cuenta) => (
      <span className="inline-flex gap-1 items-center">{etiquetaTipo(c.tipo)}{c.saldoAutomatico && <Badge tone="blue">automático</Badge>}</span>
    ), help: 'Las cuentas de MercadoPago toman el saldo disponible automáticamente de MercadoPago; si no se puede leer, se usa el último saldo cargado a mano. Con varias cuentas MercadoPago activas, el automático va a la primera creada' },
    { key: 'saldoEnLinea', header: 'Saldo automático', align: 'right' as const, render: (c: Cuenta) => <CeldaSaldoEnLinea c={c} />, help: 'Saldo disponible leído de MercadoPago (se refresca cada 10 minutos), con la hora de la lectura' },
    { key: 'activa', header: 'Estado', render: (c: Cuenta) => c.activa ? <Badge tone="green">activa</Badge> : <Badge>inactiva</Badge>, help: 'Solo las cuentas activas suman al saldo inicial' },
    { key: 'saldo', header: 'Último saldo', align: 'right' as const, render: (c: Cuenta) => c.ultimoSaldo ? <span className={c.ultimoSaldo.saldo < 0 ? 'text-red-600' : ''}>{pesos(c.ultimoSaldo.saldo)}</span> : <span className="text-slate-400">—</span> },
    { key: 'fecha', header: 'Fecha', render: (c: Cuenta) => c.ultimoSaldo ? fechaCorta(c.ultimoSaldo.fecha) : '—' },
    { key: 'cargadoPor', header: 'Cargado por', render: (c: Cuenta) => c.ultimoSaldo?.cargadoPor ?? (c.ultimoSaldo?.origen === 'automatico' ? 'automático' : '—') },
    { key: 'acciones', header: '', render: (c: Cuenta) => (
      <div className="flex gap-2 justify-end">
        <Button size="sm" disabled={!puedeEditar} title={!puedeEditar ? 'No tenés permiso para realizar esta acción' : undefined}
          onClick={() => setSaldoDe({ cuenta: c, fecha: hoyAR(), saldo: '' })}>Cargar saldo</Button>
        <Button size="sm" variant="ghost" disabled={!puedeEditar} title={!puedeEditar ? 'No tenés permiso para realizar esta acción' : undefined}
          onClick={() => setEditando({ id: c.id, nombre: c.nombre, tipo: c.tipo, activa: c.activa })}>Editar</Button>
        <Button size="sm" variant="danger" disabled={!puedeEliminar} title={!puedeEliminar ? 'No tenés permiso para realizar esta acción' : 'Eliminar la cuenta y su historial de saldos'}
          onClick={() => setBorrar(c)}>Eliminar</Button>
      </div>
    ) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <p className="text-sm text-slate-500">El saldo vigente de cada cuenta es el de fecha más reciente. Cargalo seguido{diasSaldoViejo != null && `: un saldo de más de ${diasSaldoViejo} días se marca como viejo`}.</p>
        <Button disabled={!puedeEditar} title={!puedeEditar ? 'No tenés permiso para realizar esta acción' : undefined}
          onClick={() => setEditando({ nombre: '', tipo: 'banco', activa: true })}>Nueva cuenta</Button>
      </div>
      <DataTable columns={columns} data={data ?? []} loading={loading} title="Cuentas" emptyMessage="No hay cuentas cargadas" />

      <CobrosMercadoPagoPanel onCambio={onCambio} />

      <Modal open={!!editando} cierre={cierreCuenta} title={editando?.id ? 'Editar cuenta' : 'Nueva cuenta'} size="sm">
        {editando && (
          <div className="space-y-4">
            <label className="block text-sm text-slate-700">Nombre
              <input value={editando.nombre} onChange={e => setEditando({ ...editando, nombre: e.target.value })} className={inputCls} placeholder="Ej.: Banco Galicia cta. cte." />
            </label>
            <label className="block text-sm text-slate-700">Tipo
              <select value={editando.tipo} onChange={e => setEditando({ ...editando, tipo: e.target.value as TipoCuenta })} className={inputCls}>
                {TIPOS_CUENTA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={editando.activa} onChange={e => setEditando({ ...editando, activa: e.target.checked })} className="w-4 h-4" /> Activa (suma al saldo inicial)
            </label>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="ghost" onClick={cierreCuenta.cerrar}>Cancelar</Button>
              <Button onClick={guardarCuenta} loading={guardando}>Guardar</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!saldoDe} cierre={cierreSaldo} title={saldoDe ? `Cargar saldo · ${saldoDe.cuenta.nombre}` : undefined} size="sm">
        {saldoDe && (
          <div className="space-y-4">
            <label className="block text-sm text-slate-700">Fecha
              <input type="date" max={hoyAR()} value={saldoDe.fecha} onChange={e => setSaldoDe({ ...saldoDe, fecha: e.target.value })} className={inputCls} />
            </label>
            <label className="block text-sm text-slate-700">Saldo ($)
              <input type="number" step="0.01" value={saldoDe.saldo} onChange={e => setSaldoDe({ ...saldoDe, saldo: e.target.value })} className={inputCls} placeholder="Negativo si está en descubierto" />
            </label>
            {saldoDe.cuenta.saldoAutomatico && <p className="text-xs text-slate-500">El saldo de esta cuenta se toma automáticamente de MercadoPago; el que cargues acá se usa sólo si no se puede leer.</p>}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="ghost" onClick={cierreSaldo.cerrar}>Cancelar</Button>
              <Button onClick={guardarSaldo} loading={guardando}>Guardar</Button>
            </div>
          </div>
        )}
      </Modal>

      <Confirm open={!!borrar} title="Eliminar cuenta" danger confirmLabel="Eliminar"
        message={`¿Eliminar la cuenta "${borrar?.nombre ?? ''}" y todo su historial de saldos? Para dejar de usarla conservando el historial, desactivala.`}
        onConfirm={eliminar} onCancel={() => setBorrar(null)} />
    </div>
  );
}
